/**
 * Custom hook for managing content filtering, sorting, and pagination.
 * Supports persistence of filter preferences via localStorage.
 */
import { useState, useMemo, useEffect } from 'react';
import { Content } from '../useContentData';
import { SortConfig, StatusFilter } from '../types/contentManagement';
import { filterContent, sortContent } from '../utils/contentUtils';
import { saveContentPreferences, ContentManagementPreferences } from '../utils/contentPreferences';

export interface UseContentFiltersProps {
  contents: Content[];
  itemsPerPage?: number;
  initialPreferences?: Partial<ContentManagementPreferences>;
}

export interface UseContentFiltersReturn {
  // Search state
  search: string;
  setSearch: (search: string) => void;

  // Content type filter state
  contentType: string;
  setContentType: (contentType: string) => void;

  // Status filter state
  statusFilter: StatusFilter;
  setStatusFilter: (statusFilter: StatusFilter) => void;

  // Platform filter state
  platformFilter: string;
  setPlatformFilter: (platformFilter: string) => void;

  // Sort configuration state
  sortConfig: SortConfig;
  setSortConfig: (sortConfig: SortConfig) => void;

  // Pagination state
  currentPage: number;
  setCurrentPage: (page: number) => void;

  // Computed values
  filteredContents: Content[];
  sortedContents: Content[];
  paginatedContents: Content[];
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

/**
 * Hook for managing content filters, sorting, and pagination.
 * Loads initial preferences from localStorage and persists changes.
 */
export const useContentFilters = ({
  contents,
  itemsPerPage = 12,
  initialPreferences = {}
}: UseContentFiltersProps): UseContentFiltersReturn => {
  // Filter states - initialize from saved preferences if available
  const [search, setSearch] = useState(initialPreferences.search ?? '');
  const [contentType, setContentType] = useState<string>(initialPreferences.contentType ?? 'all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(initialPreferences.statusFilter ?? 'all');
  const [platformFilter, setPlatformFilter] = useState<string>(initialPreferences.platformFilter ?? 'all');

  // Sort state - initialize from saved preferences if available
  const [sortConfig, setSortConfig] = useState<SortConfig>(
    initialPreferences.sortConfig ?? {
      key: 'created_at',
      direction: 'desc'
    }
  );

  // Pagination state - initialize from saved preferences if available
  const [currentPage, setCurrentPage] = useState(initialPreferences.currentPage ?? 1);

  // Persist search changes to localStorage
  useEffect(() => {
    saveContentPreferences({ search });
  }, [search]);

  // Persist content type changes to localStorage
  useEffect(() => {
    saveContentPreferences({ contentType });
  }, [contentType]);

  // Persist status filter changes to localStorage
  useEffect(() => {
    saveContentPreferences({ statusFilter });
  }, [statusFilter]);

  // Persist platform filter changes to localStorage
  useEffect(() => {
    saveContentPreferences({ platformFilter });
  }, [platformFilter]);

  // Persist sort configuration changes to localStorage
  useEffect(() => {
    saveContentPreferences({ sortConfig });
  }, [sortConfig]);

  // Persist current page changes to localStorage
  useEffect(() => {
    saveContentPreferences({ currentPage });
  }, [currentPage]);

  // Filter contents based on search, type, status, and platform
  const filteredContents = useMemo(() => {
    return filterContent(contents, search, contentType, statusFilter, platformFilter);
  }, [contents, search, contentType, statusFilter, platformFilter]);

  // Sort filtered contents
  const sortedContents = useMemo(() => {
    return sortContent(filteredContents, sortConfig);
  }, [filteredContents, sortConfig]);

  // Paginate sorted contents
  const paginatedContents = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedContents.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedContents, currentPage, itemsPerPage]);

  // Calculate pagination info
  const totalPages = Math.ceil(sortedContents.length / itemsPerPage);
  const hasNextPage = currentPage < totalPages;
  const hasPrevPage = currentPage > 1;

  return {
    search,
    setSearch,
    contentType,
    setContentType,
    statusFilter,
    setStatusFilter,
    platformFilter,
    setPlatformFilter,
    sortConfig,
    setSortConfig,
    currentPage,
    setCurrentPage,
    filteredContents,
    sortedContents,
    paginatedContents,
    totalPages,
    hasNextPage,
    hasPrevPage,
  };
};